<template>
  <div class="min-h-screen flex flex-col justify-center items-center pt-6 sm:pt-0 p-4">
    <logo></logo>

    <div class="w-full sm:max-w-md mt-6 px-5 py-5 sm:px-6 sm:py-4 bg-white shadow-md overflow-hidden rounded-2xl sm:rounded-lg">
      <form @submit.prevent="login" class="space-y-4">
        <div>
          <label class="block font-medium text-sm text-gray-600" for="email">
            {{ $t('auth.login.email') }}
          </label>
          <input
            v-model="data.email"
            class="mt-1.5 w-full rounded-xl border border-gray-300 bg-white px-3.5 py-3 text-base text-gray-900 shadow-sm focus:border-gray-900 focus:ring-2 focus:ring-gray-200"
            id="email"
            type="email"
            name="email"
            inputmode="email"
            autocomplete="username"
            autocapitalize="none"
            autocorrect="off"
            spellcheck="false"
            required
            autofocus
          >
        </div>
        <div>
          <label class="block font-medium text-sm text-gray-600" for="password">
            {{ $t('auth.login.password') }}
          </label>
          <div class="relative mt-1.5">
            <input
              v-model="data.password"
              class="w-full rounded-xl border border-gray-300 bg-white px-3.5 py-3 pr-12 text-base text-gray-900 shadow-sm focus:border-gray-900 focus:ring-2 focus:ring-gray-200"
              id="password"
              :type="showPassword ? 'text' : 'password'"
              name="password"
              autocomplete="current-password"
              required
            >
            <button
              type="button"
              class="absolute inset-y-0 right-0 px-3 text-sm text-gray-500 hover:text-gray-800"
              :aria-label="showPassword ? $t('auth.login.hide_password') : $t('auth.login.show_password')"
              @click="showPassword = !showPassword"
            >
              {{ showPassword ? $t('auth.login.hide_password') : $t('auth.login.show_password') }}
            </button>
          </div>
        </div>

        <div v-if="errorMessage" class="text-red-600 py-1 font-medium text-sm">
          <span>{{ errorMessage }}</span>
        </div>

        <label for="remember" class="flex items-center gap-3 py-1 select-none cursor-pointer">
          <input
            v-model="data.remember"
            id="remember"
            type="checkbox"
            class="h-5 w-5 rounded border-gray-300 text-gray-900 focus:ring-gray-300"
            name="remember"
          >
          <span class="text-sm text-gray-600">{{ $t('auth.login.remember_me') }}</span>
        </label>

        <button
          type="submit"
          class="w-full inline-flex items-center justify-center px-4 py-3.5 bg-gray-900 border border-transparent rounded-xl font-semibold text-sm text-white tracking-wide hover:bg-gray-700 active:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50 transition"
          :disabled="submitting"
        >
          {{ submitting ? $t('auth.login.submitting') : $t('auth.login.submit') }}
        </button>

        <div class="flex justify-center pt-1">
          <router-link class="underline text-sm text-gray-500 hover:text-gray-800" :to="{ name: 'ForgotPassword' }">
            {{ $t('auth.login.forgot_password') }}
          </router-link>
        </div>
      </form>
    </div>
    <div class="text-center text-sm mt-4 text-gray-600">
      {{ $t('auth.login.no_account') }}
      <router-link class="underline text-gray-500 hover:text-gray-800" :to="{ name: 'Register' }">
        {{ $t('auth.login.sign_up') }}
      </router-link>
    </div>
  </div>
</template>

<script>
import Logo from '@/components/Logo.vue'
import { useAuthStore } from '@/stores/auth.js'
import { mapActions } from 'pinia'

const REMEMBER_KEY = 'smm.auth.remember'

export default {
  data: () => {
    let remember = true
    try {
      const saved = localStorage.getItem(REMEMBER_KEY)
      if (saved === '0') remember = false
      if (saved === '1') remember = true
    } catch (_) {
      // ignore
    }

    return {
      errorMessage: null,
      submitting: false,
      showPassword: false,
      data: {
        email: '',
        password: '',
        remember,
      },
    }
  },
  components: {
    Logo,
  },
  methods: {
    ...mapActions(useAuthStore, ['attempt_user', 'set_user']),
    formatError(error) {
      const data = error?.response?.data
      if (data?.errors?.email?.[0]) return data.errors.email[0]
      if (data?.errors?.password?.[0]) return data.errors.password[0]
      if (data?.message) return data.message
      return error?.message || this.$t('common.error')
    },
    persistRememberPreference() {
      try {
        localStorage.setItem(REMEMBER_KEY, this.data.remember ? '1' : '0')
      } catch (_) {
        // ignore
      }
    },
    login() {
      this.errorMessage = null
      this.submitting = true
      this.persistRememberPreference()

      axios.get('/sanctum/csrf-cookie').then(() => {
        axios.post('/login', {
          email: this.data.email,
          password: this.data.password,
          remember: !!this.data.remember,
        })
          .then((response) => {
            if (response?.status === 200 && response.data?.two_factor) {
              this.$router.replace({ name: 'TwoFactorChallenge' })
              return
            }

            this.attempt_user()
              .then((response) => {
                if (response?.status === 200) {
                  this.$router.replace({ name: 'SmmContent' })
                }
              })
              .catch((error) => {
                this.errorMessage = this.formatError(error)
              })
          })
          .catch((error) => {
            this.errorMessage = this.formatError(error)
          })
          .finally(() => {
            this.submitting = false
          })
      }).catch((error) => {
        this.errorMessage = this.formatError(error)
        this.submitting = false
      })
    },
  },
}
</script>
